import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { WegweiserRequestParams } from './grid-widget-config.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class WegweiserApiService {
  private readonly apiUrl = environment.wegweiserApiUrl;
  private readonly http = inject(HttpClient);

  /**
   * Fetches a list of indicators based on the provided search string.
   *
   * @param {string} searchString - The string to search for indicators.
   * @return {Promise<any>} A promise that resolves to the response containing the list of indicators.
   */
  getIndicators(searchString: string) {
    return lastValueFrom(
      this.http.get<any>(`${this.apiUrl}/indicator/list?max=100`)
    );
  }

  /**
   * Fetches topic sets from the API.
   *
   * @return {Promise<any>} A promise that resolves with the data fetched from the API containing topic sets.
   */
  getTopicSets() {
    return lastValueFrom(this.http.get<any>(`${this.apiUrl}/topic/sets`));
  }

  /**
   * Fetches a list of regions based on the provided search string.
   *
   * @param {string} searchString - The search term used to filter the regions.
   * @return {Promise<any>} A promise that resolves to the list of regions.
   */
  getRegions(searchString: string) {
    return lastValueFrom(
      this.http.get<any>(
        `${this.apiUrl}/region/list?max=30&search=${searchString}`
      )
    );
  }

  /**
   * Retrieves geographical data based on the provided region name and type.
   *
   * @param {string} regionName - The name of the region for which to fetch geographical data.
   * @param {string} type - The type of geographical layer to be used (e.g., 'GEMEINDE', 'DISTRICT').
   * @return {Promise<any>} A promise that resolves to the geographical data for the specified region.
   */
  getGeoDataByRegionName(regionName: string, type: string) {
    const layer = type === 'GEMEINDE' ? 'COMMUNE' : 'DISTRICT';
    return lastValueFrom(
      this.http.get<any>(
        `${this.apiUrl}/map/data/${regionName}?layer=${layer}&outline=LANDKREIS`
      )
    );
  }

  /**
   * Fetches geographical data for the specified region IDs.
   *
   * @param {number[]} ids - An array of region IDs for which the geographical data is to be retrieved.
   * @return {Promise<any>} A promise that resolves with the geographical data for the specified regions.
   */
  getGeoDataByRegionIds(ids: number[]) {
    return lastValueFrom(
      this.http.post<any>(`${this.apiUrl}/map/data`, { regionIds: ids })
    );
  }

  /**
   * Fetches statistical data from the API based on the provided request options.
   *
   * @param {WegweiserRequestParams} requestOptions - The parameters to be sent in the API request.
   * @return {Promise<any>} A promise that resolves with the API response data.
   */
  getData(requestOptions: WegweiserRequestParams): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(`${this.apiUrl}/statistics/data`, requestOptions)
    );
  }
}
